import { useState } from 'react';
import type { AppointmentType } from '../../../../mocks/appointments';

interface Props {
  types: AppointmentType[];
  addType: (
    t: Omit<AppointmentType, 'id' | 'created_at'>
  ) => Promise<AppointmentType | null>;
  updateType: (
    id: string,
    u: Partial<AppointmentType>
  ) => Promise<AppointmentType | null>;
  deleteType: (id: string) => Promise<boolean>;
}

const categoryLabels: Record<string, string> = {
  'alta-costura': 'Alta Costura',
  'vestidos-novia': 'Vestidos de Novia',
  'pret-a-porter': 'Prêt-à-Porter',
};

export default function AppointmentTypesManager({
  types,
  addType,
  updateType,
  deleteType,
}: Props) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState(0);
  const [category, setCategory] = useState('alta-costura');
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [editTypeName, setEditTypeName] = useState('');
  const [editTypeDesc, setEditTypeDesc] = useState('');
  const [editTypeDuration, setEditTypeDuration] = useState(60);
  const [editTypePrice, setEditTypePrice] = useState(0);
  const [editTypeCategory, setEditTypeCategory] = useState('alta-costura');
  const [savingType, setSavingType] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const startEdit = (t: AppointmentType) => {
    setEditingTypeId(t.id);
    setEditTypeName(t.name);
    setEditTypeDesc(t.description || '');
    setEditTypeDuration(t.duration_minutes);
    setEditTypePrice(t.price || 0);
    setEditTypeCategory(t.category || 'alta-costura');
  };

  const saveEdit = async (id: string) => {
    if (!editTypeName.trim()) return;
    setSavingType(true);
    await updateType(id, {
      name: editTypeName.trim(),
      description: editTypeDesc.trim() || undefined,
      duration_minutes: editTypeDuration,
      price: editTypePrice || undefined,
      category: editTypeCategory,
    });
    setSavingType(false);
    setEditingTypeId(null);
  };

  const handleAdd = async () => {
    if (!name) return;
    await addType({
      name,
      description: desc || undefined,
      duration_minutes: duration,
      color: '#1a1a1a',
      is_active: true,
      price: price || undefined,
      category,
    });
    setName('');
    setDesc('');
    setDuration(60);
    setPrice(0);
    setCategory('alta-costura');
  };

  const filteredTypes =
    categoryFilter === 'all'
      ? types
      : types.filter((t) => (t.category || 'alta-costura') === categoryFilter);

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-brand-black text-2xl font-medium">
        Tipos de cita
      </h2>
      <div className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Nombre *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm"
            />
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Duración (min)
            </label>
            <input
              type="number"
              min={15}
              step={15}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm"
            />
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Precio (€)
            </label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm"
              placeholder="0 = gratuita"
            />
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Categoría
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm bg-white"
            >
              <option value="alta-costura">Alta Costura</option>
              <option value="vestidos-novia">Vestidos de Novia</option>
              <option value="pret-a-porter">Prêt-à-Porter</option>
            </select>
          </div>
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Descripción
            </label>
            <input
              type="text"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm"
            />
          </div>
        </div>
        <button
          onClick={handleAdd}
          className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
        >
          + Añadir tipo
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setCategoryFilter('all')}
          className={`px-3 py-1.5 rounded-full font-sans text-xs transition-colors whitespace-nowrap ${
            categoryFilter === 'all'
              ? 'bg-brand-black text-white'
              : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
          }`}
        >
          Todas
        </button>
        <button
          onClick={() => setCategoryFilter('alta-costura')}
          className={`px-3 py-1.5 rounded-full font-sans text-xs transition-colors whitespace-nowrap ${
            categoryFilter === 'alta-costura'
              ? 'bg-brand-black text-white'
              : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
          }`}
        >
          Alta Costura
        </button>
        <button
          onClick={() => setCategoryFilter('vestidos-novia')}
          className={`px-3 py-1.5 rounded-full font-sans text-xs transition-colors whitespace-nowrap ${
            categoryFilter === 'vestidos-novia'
              ? 'bg-brand-black text-white'
              : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
          }`}
        >
          Vestidos de Novia
        </button>
        <button
          onClick={() => setCategoryFilter('pret-a-porter')}
          className={`px-3 py-1.5 rounded-full font-sans text-xs transition-colors whitespace-nowrap ${
            categoryFilter === 'pret-a-porter'
              ? 'bg-brand-black text-white'
              : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
          }`}
        >
          Prêt-à-Porter
        </button>
      </div>

      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-brand-gray-50">
            <tr>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Nombre
              </th>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Categoría
              </th>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Duración
              </th>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Precio
              </th>
              <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Activo
              </th>
              <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-gray-200">
            {filteredTypes.map((t) => {
              const isEditing = editingTypeId === t.id;
              return (
                <tr
                  key={t.id}
                  className="hover:bg-brand-gray-50/50 transition-colors"
                >
                  {isEditing ? (
                    <>
                      <td className="px-5 py-3">
                        <input
                          type="text"
                          value={editTypeName}
                          onChange={(e) => setEditTypeName(e.target.value)}
                          className="w-full border border-brand-gray-200 rounded-md px-2 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                          autoFocus
                        />
                        <input
                          type="text"
                          value={editTypeDesc}
                          onChange={(e) => setEditTypeDesc(e.target.value)}
                          className="w-full border border-brand-gray-200 rounded-md px-2 py-1.5 font-sans text-sm mt-1.5 focus:outline-none focus:border-brand-black"
                          placeholder="Descripción (opcional)"
                        />
                      </td>
                      <td className="px-5 py-3">
                        <select
                          value={editTypeCategory}
                          onChange={(e) => setEditTypeCategory(e.target.value)}
                          className="w-full border border-brand-gray-200 rounded-md px-2 py-1.5 font-sans text-sm bg-white focus:outline-none focus:border-brand-black"
                        >
                          <option value="alta-costura">Alta Costura</option>
                          <option value="vestidos-novia">
                            Vestidos de Novia
                          </option>
                          <option value="pret-a-porter">
                            Prêt-à-Porter
                          </option>
                        </select>
                      </td>
                      <td className="px-5 py-3">
                        <input
                          type="number"
                          min={15}
                          step={15}
                          value={editTypeDuration}
                          onChange={(e) =>
                            setEditTypeDuration(Number(e.target.value))
                          }
                          className="w-24 border border-brand-gray-200 rounded-md px-2 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                        />
                      </td>
                      <td className="px-5 py-3">
                        <input
                          type="number"
                          min={0}
                          step={0.01}
                          value={editTypePrice}
                          onChange={(e) =>
                            setEditTypePrice(Number(e.target.value))
                          }
                          className="w-24 border border-brand-gray-200 rounded-md px-2 py-1.5 font-sans text-sm focus:outline-none focus:border-brand-black"
                        />
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`font-sans text-xs font-medium px-2 py-1 rounded ${
                            t.is_active
                              ? 'bg-emerald-50 text-emerald-600'
                              : 'bg-brand-gray-100 text-brand-gray-500'
                          }`}
                        >
                          {t.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => saveEdit(t.id)}
                            disabled={savingType || !editTypeName.trim()}
                            className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                            title="Guardar"
                          >
                            <i className="ri-check-line" />
                          </button>
                          <button
                            onClick={() => setEditingTypeId(null)}
                            className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-gray-200 transition-colors"
                            title="Cancelar"
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-5 py-3">
                        <p className="font-sans text-brand-black text-sm font-medium">
                          {t.name}
                        </p>
                        {t.description && (
                          <p className="font-sans text-brand-gray-400 text-xs">
                            {t.description}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className="font-sans text-xs font-medium px-2 py-1 rounded bg-brand-gray-100 text-brand-gray-600">
                          {categoryLabels[t.category || 'alta-costura'] ||
                            t.category ||
                            'Alta Costura'}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-sans text-brand-gray-600 text-sm">
                        {t.duration_minutes} min
                      </td>
                      <td className="px-5 py-3 font-sans text-brand-black text-sm font-medium">
                        {t.price ? `€${t.price.toFixed(2)}` : 'Gratuita'}
                      </td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() =>
                            updateType(t.id, { is_active: !t.is_active })
                          }
                          className={`w-10 h-5 rounded-full transition-colors relative ${
                            t.is_active ? 'bg-brand-sage' : 'bg-brand-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                              t.is_active ? 'left-5' : 'left-0.5'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => startEdit(t)}
                            className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                            title="Editar"
                          >
                            <i className="ri-pencil-line" />
                          </button>
                          <button
                            onClick={() => deleteType(t.id)}
                            className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                            title="Eliminar"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredTypes.length === 0 && (
          <div className="p-8 text-center font-sans text-brand-gray-400 text-sm">
            No hay tipos configurados
          </div>
        )}
      </div>
    </div>
  );
}